package prescription.components

import antd.button.button
import antd.comment.comment
import antd.descriptions.descriptions
import antd.descriptions.descriptionsItem
import antd.form.form
import antd.form.formItem
import antd.input.input
import antd.input.textArea
import antd.list.ListComponent
import antd.list.list
import antd.message.message
import antd.modal.modal
import antd.pageheader.pageHeader
import antd.tabs.tabPane
import antd.tabs.tabs
import com.jingfangpai.common.orm.mapping.Prescription
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentType
import external.toJsObject
import kotlinext.js.Object
import kotlinx.browser.window
import react.*
import react.dom.div
import react.dom.li
import react.router.dom.useHistory
import setTitle
import prescription.containers.PrescriptionDetailPageContext
import antd.avatar.avatar
import antd.card.card
import antd.icon.editOutlined
import antd.icon.userOutlined
import com.jingfangpai.common.data.type.DateTime
import com.jingfangpai.prescription.model.PrescriptionDetail
import kotlinext.js.js
import kotlinx.html.style
import react.dom.jsStyle
import react.dom.svg
import urlBase
import user.service.AuthService

external interface PrescriptionDetailProps : RProps {
    var id: String
}

val PrescriptionInDetail = functionalComponent<PrescriptionDetailProps> { props ->

    val ctx = useContext(PrescriptionDetailPageContext)
    val history = useHistory()
    val (prescription, setPrescription) = useState(PrescriptionDetail())
    val (notes, setNotes) = useState(arrayOf<Comment>())
    val (medicineIdNames, setMedicineIdNames) = useState(mapOf<String, String>())
    val (showNoteModal, setShowNoteModal) = useState(false)
    val (editingNote, setEditingNote) = useState<Comment?>(null)
    val (noteForm, setNoteForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)

    useEffect(props.id) {
        if (32 == props.id?.length ?: 0) {
            ctx.getDetail(props.id, { resp ->
                if (resp.ok() && resp.data != null) {
                    setPrescription(resp.data!!)
                    setTitle(resp.data!!.name ?: "")
                } else {
                    console.error("Failed to fetch prescription detail:", resp)
                    message.error("获取方剂失败: ${resp.message ?: "未知错误"}")
                }
            }, { error ->
                console.error("Exception fetching prescription detail:", error)
                console.error("Error stack:", error.printStackTrace())
                val errorMsg = error.message ?: error.toString() ?: "未知错误"
                message.error("获取方剂失败: $errorMsg")
            })

            // Fetch notes for this prescription
            ctx.getComments(CommentEntityType.Prescription, props.id, CommentType.笔记, { resp ->
                if (resp.ok() && resp.data != null) {
                    setNotes(resp.data!!)
                }
            }, { error ->
                console.error("Failed to fetch notes:", error)
            })

            // Fetch medicine ID names for composition links
            ctx.getMedicineIdNames({ resp ->
                if (resp.ok() && resp.data != null) {
                    val medicineMap = resp.data!!
                        .filter { !it.name.isNullOrBlank() && !it.id.isNullOrBlank() }
                        .associate { it.name!! to it.id!! }
                    setMedicineIdNames(medicineMap)
                }
            }, { error ->
                console.error("Failed to fetch medicine ID names:", error)
            })
        }
    }

    fun openAddNoteModal() {
        setEditingNote(null)
        setSaveError(null)
        setNoteForm(Comment().apply {
            entityType = CommentEntityType.Prescription
            entityId = props.id
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
            createdBy = AuthService.getUserAuthData()?.userName ?: ""
            weight = 0
        })
        setShowNoteModal(true)
    }

    fun openEditNoteModal(note: Comment) {
        setEditingNote(note)
        setSaveError(null)
        setNoteForm(Object.assign(Comment(), note))
        setShowNoteModal(true)
    }

    fun saveNote() {
        if (noteForm.comment.isNullOrBlank()) {
            setSaveError("请填写笔记内容")
            return
        }

        // Ensure entityType is Prescription and set author info
        noteForm.entityType = CommentEntityType.Prescription
        noteForm.entityId = props.id
        noteForm.type = CommentType.笔记
        noteForm.authorId = AuthService.getUserAuthData()?.id ?: ""

        // Set createdOn timestamp for new notes
        if (noteForm.createdOn == null) {
            noteForm.createdOn = DateTime()
        }

        setSaving(true)
        setSaveError(null)
        ctx.saveComment(noteForm, { resp ->
            setSaving(false)
            if (resp.ok()) {
                // Refresh notes list
                ctx.getComments(CommentEntityType.Prescription, props.id, CommentType.笔记, { refreshResp ->
                    if (refreshResp.ok() && refreshResp.data != null) {
                        setNotes(refreshResp.data!!)
                    }
                    setShowNoteModal(false)
                    // Reset form after successful save
                    setNoteForm(Comment())
                    setEditingNote(null)
                }, {})
            } else {
                setSaveError("抱歉，出错了：${resp.message}")
            }
        }, {
            setSaving(false)
            console.log(it)
            setSaveError("保存失败，${it.message}")
        })
    }

    // Function to render composition with clickable medicine links
    fun renderCompositionWithLinks(composition: String): dynamic {
        // Strip existing HTML tags first to avoid duplicated links
        val cleanComposition = composition.replace(Regex("<[^>]*>"), "")
        
        // Sort medicine names by length (longest first) to avoid partial matches
        val sortedMedicines = medicineIdNames.keys.sortedByDescending { it.length }
        
        // Find all non-overlapping matches
        data class Match(val start: Int, val end: Int, val html: String)
        val matches = mutableListOf<Match>()
        
        sortedMedicines.forEach { medicineName ->
            if (medicineName.isBlank()) return@forEach
            val medicineId = medicineIdNames[medicineName]
            if (!medicineId.isNullOrBlank()) {
                val linkHtml = "<a href=\"$urlBase/learning/medicine/detail/$medicineId\" target=\"_blank\" style=\"color: #1890ff; text-decoration: underline; cursor: pointer;\">$medicineName</a>"
                val escapedName = Regex.escape(medicineName)
                val regex = Regex(escapedName, RegexOption.IGNORE_CASE)
                
                regex.findAll(cleanComposition).forEach { matchResult ->
                    val startIndex = matchResult.range.first
                    val endIndex = matchResult.range.last + 1
                    
                    // Check if this position overlaps with existing matches
                    val overlaps = matches.any {
                        !(startIndex >= it.end || endIndex <= it.start)
                    }
                    
                    if (!overlaps) {
                        matches.add(Match(startIndex, endIndex, linkHtml))
                    }
                }
            }
        }
        
        // Sort matches by start position
        matches.sortBy { it.start }
        
        // Build result by concatenating segments
        val result = StringBuilder()
        var lastIndex = 0
        
        matches.forEach { match ->
            // Add text before this match
            if (match.start > lastIndex) {
                result.append(cleanComposition.substring(lastIndex, match.start))
            }
            // Add the HTML link
            result.append(match.html)
            lastIndex = match.end
        }
        
        // Add remaining text
        if (lastIndex < cleanComposition.length) {
            result.append(cleanComposition.substring(lastIndex))
        }
        
        val finalHtml = result.toString().replace("\n", "<br>")
        return js { __html = finalHtml }
    }

    // Function to render any text with clickable medicine links
    fun renderTextWithMedicineLinks(text: String): dynamic {
        // Strip existing HTML tags first to avoid duplicated links
        val cleanText = text.replace(Regex("<[^>]*>"), "")
        
        // Sort medicine names by length (longest first) to avoid partial matches
        val sortedMedicines = medicineIdNames.keys.sortedByDescending { it.length }
        
        // Find all non-overlapping matches
        data class Match(val start: Int, val end: Int, val html: String)
        val matches = mutableListOf<Match>()
        
        sortedMedicines.forEach { medicineName ->
            if (medicineName.isBlank()) return@forEach
            val medicineId = medicineIdNames[medicineName]
            if (!medicineId.isNullOrBlank()) {
                val linkHtml = "<a href=\"$urlBase/learning/medicine/detail/$medicineId\" target=\"_blank\" style=\"color: #1890ff; text-decoration: underline; cursor: pointer;\">$medicineName</a>"
                val escapedName = Regex.escape(medicineName)
                val regex = Regex(escapedName, RegexOption.IGNORE_CASE)
                
                regex.findAll(cleanText).forEach { matchResult ->
                    val startIndex = matchResult.range.first
                    val endIndex = matchResult.range.last + 1
                    
                    // Check if this position overlaps with existing matches
                    val overlaps = matches.any {
                        !(startIndex >= it.end || endIndex <= it.start)
                    }
                    
                    if (!overlaps) {
                        matches.add(Match(startIndex, endIndex, linkHtml))
                    }
                }
            }
        }
        
        // Sort matches by start position
        matches.sortBy { it.start }
        
        // Build result by concatenating segments
        val result = StringBuilder()
        var lastIndex = 0
        
        matches.forEach { match ->
            // Add text before this match
            if (match.start > lastIndex) {
                result.append(cleanText.substring(lastIndex, match.start))
            }
            // Add the HTML link
            result.append(match.html)
            lastIndex = match.end
        }
        
        // Add remaining text
        if (lastIndex < cleanText.length) {
            result.append(cleanText.substring(lastIndex))
        }
        
        val finalHtml = result.toString().replace("\n", "<br>")
        return js { __html = finalHtml }
    }

    div("PrescriptionDetail") {
        attrs.jsStyle = kotlinext.js.js {
            margin = "0 auto"
            maxWidth = "1920px"
        }

        pageHeader {
            attrs {
                className = "prescription-detail-page-header-responsive"
                onBack = {
                    window.history.back()
                }
                title = "${prescription.name ?: ""}"
                subTitle = ""
                extra = arrayOf(buildElement {
                    button {
                        attrs {
                            onClick = {
                                history.push(SmartTcmRoute.PrescriptionEdit.link.url.replace(":id", prescription.id ?: ""))
                            }
                        }
                        +"编辑"
                    }
                })
            }
        }

        div {
            // Basic Information Section
            card {
                attrs {
                    title = "基本信息"
                    style = kotlinext.js.js {
                        marginBottom = "16px"
                    }
                }

                descriptions {
                    attrs {
                        bordered = true
                        column = 2
                    }

                    // Combine category and source into one field
                    val category = prescription.category?.getLiteral()
                    val subCategory = prescription.subCategory?.getLiteral()
                    val source = prescription.source
                    
                    if (!category.isNullOrBlank() || !subCategory.isNullOrBlank() || !source.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "分类"
                                span = 2
                            }
                            val categoryDisplay = if (!category.isNullOrBlank() && !subCategory.isNullOrBlank()) {
                                "$category->$subCategory"
                            } else if (!category.isNullOrBlank()) {
                                category
                            } else {
                                subCategory ?: ""
                            }
                            
                            val fullDisplay = if (!categoryDisplay.isEmpty() && !source.isNullOrBlank()) {
                                "$categoryDisplay -- 出自《$source》"
                            } else if (!categoryDisplay.isEmpty()) {
                                categoryDisplay
                            } else if (!source.isNullOrBlank()) {
                                "出自《$source》"
                            } else {
                                ""
                            }
                            +fullDisplay
                        }
                    }

                    val composition = prescription.composition
                    if (!composition.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "组成"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = renderCompositionWithLinks(composition)
                            }
                        }
                    }

                    val administration = prescription.administration
                    if (!administration.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "用法"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = js { __html = administration.replace("\n", "<br>") }
                            }
                        }
                    }

                    val actions = prescription.actions
                    if (!actions.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "功效"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = js { __html = actions.replace("\n", "<br>") }
                            }
                        }
                    }

                    val indications = prescription.indications
                    val keySignSymptoms = prescription.keySignSymptoms
                    if (!indications.isNullOrBlank() || !keySignSymptoms.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "主治"
                                span = 2
                            }
                            div {
                                val displayText = if (!indications.isNullOrBlank() && !keySignSymptoms.isNullOrBlank()) {
                                    "$indications（常见体征症状：$keySignSymptoms）"
                                } else if (!indications.isNullOrBlank()) {
                                    indications
                                } else {
                                    "常见体征症状：$keySignSymptoms"
                                }
                                attrs["dangerouslySetInnerHTML"] = js { __html = displayText.replace("\n", "<br>") }
                            }
                        }
                    }

                    val contraindication = prescription.contraindication
                    if (!contraindication.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "禁忌"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = js { __html = contraindication.replace("\n", "<br>") }
                            }
                        }
                    }

                    val formulaAnalysis = prescription.formulaAnalysis
                    if (!formulaAnalysis.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "方解"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = renderTextWithMedicineLinks(formulaAnalysis)
                            }
                        }
                    }

                    val applications = prescription.applications
                    if (!applications.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "应用"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = renderTextWithMedicineLinks(applications)
                            }
                        }
                    }

                    val differentiation = prescription.differentiation
                    if (!differentiation.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "相近功效方剂比较鉴别"
                                span = 2
                            }
                            div {
                                attrs["dangerouslySetInnerHTML"] = renderTextWithMedicineLinks(differentiation)
                            }
                        }
                    }
                }
            }

            // Other Prescriptions of Same Category Section
            if (prescription.categoryOthers.isNotEmpty()) {
                card {
                    attrs {
                        title = "同类其他方剂"
                        style = kotlinext.js.js {
                            marginBottom = "16px"
                        }
                    }
                    
                    // Group by subCategory and display
                    prescription.categoryOthers.forEach { (subCategory, otherPrescriptions) ->
                        div {
                            attrs.jsStyle = kotlinext.js.js {
                                marginBottom = "12px"
                            }
                            
                            // Display subCategory label if it exists
                            if (subCategory != null) {
                                div {
                                    attrs.jsStyle = kotlinext.js.js {
                                        fontWeight = "bold"
                                        fontSize = "14px"
                                        marginBottom = "8px"
                                        color = "#666"
                                    }
                                    +subCategory.getLiteral()
                                }
                            }
                            
                            div {
                                attrs.jsStyle = kotlinext.js.js {
                                    display = "flex"
                                    flexWrap = "wrap"
                                    gap = "8px"
                                    paddingLeft = if (subCategory == null) "0" else "12px"
                                }
                                
                                otherPrescriptions.forEach { otherPrescription ->
                                    button {
                                        attrs {
                                            type = "link"
                                            style = kotlinext.js.js {
                                                padding = "4px 12px"
                                                fontSize = "14px"
                                            }
                                            onClick = {
                                                if (!otherPrescription.id.isNullOrBlank()) {
                                                    history.push("$urlBase/learning/prescription/detail/${otherPrescription.id}")
                                                }
                                            }
                                        }
                                        +(otherPrescription.name ?: "")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Notes Section
            card {
                attrs {
                    title = "笔记"
                }

                div {
                    button {
                        attrs {
                            type = "primary"
                            style = kotlinext.js.js {
                                marginBottom = "16px"
                            }
                            onClick = {
                                openAddNoteModal()
                            }
                        }
                        +"添加笔记"
                    }

                    if (notes.isEmpty()) {
                        div {
                            +"暂无笔记"
                        }
                    } else {
                        list<Comment, ListComponent<Comment>> {
                            attrs {
                                className = "comment-list"
                                header = "${notes.size}条笔记"
                                itemLayout = "horizontal"
                                dataSource = notes
                                renderItem = { noteItem, index ->
                                    buildElement {
                                        li {
                                            comment {
                                                attrs {
                                                    author = noteItem.createdBy

                                                    content = buildElement {
                                                        div {
                                                            if (!noteItem.title.isNullOrBlank()) {
                                                                div {
                                                                    +noteItem.title!!
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                        fontWeight = "bold"
                                                                        marginBottom = "8px"
                                                                        display = "block"
                                                                    }
                                                                }
                                                            }
                                                            div {
                                                                +(noteItem.comment ?: "")
                                                                attrs.jsStyle = kotlinext.js.js {
                                                                    whiteSpace = "pre-wrap"
                                                                }
                                                            }
                                                            if (!noteItem.description.isNullOrBlank()) {
                                                                div {
                                                                    +"（来源：${noteItem.description}）"
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                        marginTop = "8px"
                                                                        fontSize = "12px"
                                                                        color = "#666"
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                    datetime = noteItem.createdOn?.toDateTimeString()
                                                    actions = arrayOf(
                                                        buildElement {
                                                            button {
                                                                attrs {
                                                                    type = "link"
                                                                    size = "small"
                                                                    icon = buildElement {
                                                                        editOutlined {}
                                                                    }
                                                                    onClick = {
                                                                        openEditNoteModal(noteItem)
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    )
                                                    attrs.avatar = buildElement {
                                                        avatar {
                                                            attrs {
                                                                style = kotlinext.js.js {
                                                                    backgroundColor = "#87d068"
                                                                }
                                                                shape = "square"
                                                                icon = buildElement {
                                                                    userOutlined {}
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Note Modal
        modal {
            attrs {
                title = if (editingNote != null) "编辑笔记" else "添加笔记"
                visible = showNoteModal
                onCancel = {
                    if (!saving) {
                        setShowNoteModal(false)
                    }
                }
                onOk = {
                    saveNote()
                }
                okText = if (saving) "保存中..." else "保存"
                cancelText = "取消"
                okButtonProps = kotlinext.js.js {
                    loading = saving
                    disabled = saving
                }
            }

            form {
                attrs {
                    layout = "vertical"
                }

                // Show error message if any
                if (saveError != null) {
                    div {
                        attrs.style = kotlinext.js.js {
                            color = "#ff4d4f"
                            backgroundColor = "#fff2f0"
                            border = "1px solid #ffccc7"
                            padding = "8px 12px"
                            marginBottom = "16px"
                            borderRadius = "4px"
                        }
                        +saveError!!
                    }
                }

                formItem {
                    attrs {
                        label = "笔记标题"
                    }
                    input {
                        attrs {
                            value = noteForm.title ?: ""
                            placeholder = "请填写笔记标题（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newTitle = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        title = newTitle
                                    }
                                }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "笔记内容"
                    }
                    textArea {
                        attrs {
                            value = noteForm.comment ?: ""
                            rows = 8
                            placeholder = "请填写笔记内容"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newComment = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        comment = newComment
                                    }
                                }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "来源"
                    }
                    input {
                        attrs {
                            value = noteForm.description ?: ""
                            placeholder = "请填写笔记来源（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newDescription = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        description = newDescription
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
